/* eslint-disable react/no-unstable-nested-components */

import { useMemo } from 'react';

import BorderColorIcon from '@mui/icons-material/BorderColor';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import {
    Divider,
    FormControl,
    IconButton,
    Input,
    InputLabel,
    Link,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Slider,
    Stack,
    Switch,
    Typography,
} from '@mui/material';

import {
    ChartConfig,
    ChartDsColor,
} from 'components/organisms/InteractiveChart';
import { usePageStorage } from 'hooks/api/builder/usePageStorage';
import { useAppSelector } from 'hooks/store/useAppStore';
import { useParseChart } from 'hooks/utils/parseChart';
import { InteractiveChartConfig } from 'types/builder/componentConfig';

import { FileUploadControl } from '../../editorControls/FileUploadControl';
import { ChartColorPicker } from './ColorPicker';

type InteractiveChartControlProps = {
    node?: InteractiveChartConfig;
    onChange: (config: InteractiveChartConfig) => void;
};

export const InteractiveChartControl = ({
    onChange,
    node,
}: InteractiveChartControlProps): JSX.Element => {
    const { config } = node?.props || {};

    const {
        type = 'bar',
        chartColors = [],
        group = false,
        height = 1,
        indexAxis = 'x',
        legendPos = 'top',
        parsingOption = 'index',
        stack = false,
        gridX = true,
        gridY = true,
    } = config || {};

    const onConfigChange = (newConf?: Partial<ChartConfig>): void => {
        onChange({
            component: 'interactiveChart',
            props: {
                ...node?.props,
                config: {
                    type,
                    ...config,
                    ...newConf,
                },
            },
        });
    };

    const { id: pageID } =
        useAppSelector((state) => state.pageBuilder.config) || {};

    const { file, ...conf } = config || {};

    const { findFile } = usePageStorage(pageID);

    const selectedFile = useMemo(() => {
        if (file) {
            return findFile(file)?.file;
        }
        return undefined;
    }, [file, findFile]);

    const {
        data: { datasets },
        loading,
    } = useParseChart({ ...conf, type, file: selectedFile });

    const changeLabelColor = (label: ChartDsColor): void => {
        const currentLabel = chartColors.find((el) => el.label === label.label);

        onConfigChange({
            chartColors: [
                ...chartColors.filter((el) => el.label !== label.label),
                { ...currentLabel, ...label },
            ],
        });
    };

    return (
        <Stack spacing={2}>
            <Stack
                sx={{
                    pointerEvents:
                        loading && datasets.length ? 'none' : undefined,
                    opacity: loading && datasets.length ? 0.5 : undefined,
                }}
            >
                <Typography>Источник</Typography>
                <FileUploadControl
                    accept="tables"
                    id={pageID}
                    onSuccess={(f) => {
                        onConfigChange({
                            file: f.uuid,
                            chartColors: [],
                            group: false,
                            stack: false,
                            type: 'bar',
                        });
                    }}
                />
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption">
                        {selectedFile ? 'Файл загружен' : 'Файл не загружен'}
                    </Typography>
                    {selectedFile ? (
                        <Link
                            href={selectedFile}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontSize={12}
                        >
                            Предпросмотр
                        </Link>
                    ) : undefined}
                </Stack>
            </Stack>
            <FormControl variant="outlined" size="small">
                <InputLabel id="chart-key-label">Ключевые значения</InputLabel>
                <Select
                    labelId="chart-key-label"
                    label="Ключевые значения"
                    value={parsingOption || ''}
                    disabled={loading}
                >
                    <MenuItem
                        value="index"
                        onClick={() =>
                            onConfigChange({
                                parsingOption: 'index',
                                chartColors: [],
                                group: false,
                                stack: false,
                                type: 'bar',
                            })
                        }
                    >
                        Первый столбец
                    </MenuItem>
                    <MenuItem
                        value="rowMapping"
                        onClick={() =>
                            onConfigChange({ parsingOption: 'rowMapping' })
                        }
                    >
                        Первый ряд
                    </MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined" size="small">
                <InputLabel id="chart-type-label">Вариант</InputLabel>
                <Select
                    labelId="chart-type-label"
                    label="Вариант"
                    value={type || ''}
                    disabled={loading}
                >
                    <MenuItem
                        value="line"
                        onClick={() => onConfigChange({ type: 'line' })}
                    >
                        Линейный
                    </MenuItem>
                    <MenuItem
                        value="bar"
                        onClick={() => onConfigChange({ type: 'bar' })}
                    >
                        Столбчатый
                    </MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined" size="small">
                <InputLabel id="chart-dir-label">Направление</InputLabel>
                <Select
                    labelId="chart-dir-label"
                    label="Направление"
                    value={indexAxis || ''}
                    disabled={loading}
                >
                    <MenuItem
                        value="x"
                        onClick={() => onConfigChange({ indexAxis: 'x' })}
                    >
                        Горизонтальное
                    </MenuItem>
                    <MenuItem
                        value="y"
                        onClick={() => onConfigChange({ indexAxis: 'y' })}
                    >
                        Вертикальное
                    </MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined" size="small">
                <InputLabel id="chart-legend-label">
                    Расположение легенд
                </InputLabel>
                <Select
                    labelId="chart-legend-label"
                    label="Расположение легенд"
                    value={legendPos || ''}
                    disabled={loading}
                >
                    <MenuItem
                        value="top"
                        onClick={() => onConfigChange({ legendPos: 'top' })}
                    >
                        Сверху
                    </MenuItem>
                    <MenuItem
                        value="bottom"
                        onClick={() => onConfigChange({ legendPos: 'bottom' })}
                    >
                        Снизу
                    </MenuItem>
                    <MenuItem
                        value="left"
                        onClick={() => onConfigChange({ legendPos: 'left' })}
                    >
                        Слева
                    </MenuItem>
                    <MenuItem
                        value="right"
                        onClick={() => onConfigChange({ legendPos: 'right' })}
                    >
                        Справа
                    </MenuItem>
                </Select>
            </FormControl>
            <Divider />
            <Typography>Высота графика</Typography>
            <Stack sx={{ px: 1 }} direction="row" spacing={1}>
                <Slider
                    value={height}
                    disabled={loading}
                    max={100}
                    min={1}
                    marks
                    size="small"
                    onChange={(e, v) => {
                        onConfigChange({
                            height: typeof v === 'number' ? v : undefined,
                        });
                    }}
                />
                <Input
                    value={height}
                    size="small"
                    onChange={(e) => {
                        onConfigChange({
                            height: Number(e.target.value),
                        });
                    }}
                    inputProps={{
                        step: 5,
                        min: 1,
                        max: 100,
                        type: 'number',
                    }}
                />
            </Stack>
            <Divider />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography>Соединить данные</Typography>
                <Switch
                    disabled={loading}
                    checked={stack}
                    onChange={(e, v) => onConfigChange({ stack: v })}
                />
            </Stack>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography>Группированный</Typography>
                <Switch
                    disabled={loading}
                    checked={group}
                    onChange={(e, v) => onConfigChange({ group: v })}
                />
            </Stack>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography>Сетка шкалы X</Typography>
                <Switch
                    disabled={loading}
                    checked={gridX}
                    onChange={(e, v) => onConfigChange({ gridX: v })}
                />
            </Stack>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography>Сетка шкалы Y</Typography>
                <Switch
                    disabled={loading}
                    checked={gridY}
                    onChange={(e, v) => onConfigChange({ gridY: v })}
                />
            </Stack>
            {datasets.length && !loading ? (
                <>
                    <Divider />
                    <List disablePadding>
                        {datasets.map((ds, i) => {
                            const currLabel = chartColors.find(
                                (el) => el.label === ds.label
                            );

                            return (
                                <ListItem key={i} disablePadding>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ width: '100%' }}
                                    >
                                        <ListItemText
                                            primary={ds.label}
                                            sx={{ mr: 'auto' }}
                                        />
                                        {type === 'line' ? (
                                            <ChartColorPicker
                                                button={(onClick) => (
                                                    <IconButton
                                                        disabled={loading}
                                                        onClick={onClick}
                                                    >
                                                        <BorderColorIcon
                                                            sx={{
                                                                fill: currLabel?.borderColor,
                                                            }}
                                                        />
                                                    </IconButton>
                                                )}
                                                color={currLabel?.borderColor}
                                                onColorChange={(c) => {
                                                    changeLabelColor({
                                                        label: ds.label || '',
                                                        borderColor: c,
                                                    });
                                                }}
                                            />
                                        ) : undefined}
                                        <ChartColorPicker
                                            button={(onClick) => (
                                                <IconButton
                                                    edge="end"
                                                    disabled={loading}
                                                    onClick={onClick}
                                                >
                                                    <ColorLensIcon
                                                        sx={{
                                                            fill: currLabel?.backgroundColor,
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                            color={currLabel?.backgroundColor}
                                            onColorChange={(c) => {
                                                changeLabelColor({
                                                    label: ds.label || '',
                                                    backgroundColor: c,
                                                });
                                            }}
                                        />
                                    </Stack>
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            ) : undefined}
        </Stack>
    );
};
